import type { Dictionary } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { LoadingStatus } from '~/Slices/schema';
import type { IAppState } from '~/appstate';
import type { ISupplier } from '../domain';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '../fetch';
import { LocalStorage } from '../helpers';
import * as Hub from '../ogm-hub';

export interface ISupplierState {
	customerSuppliersByCode: Dictionary<ISupplier>;
	status: LoadingStatus;
}

const initialState: ISupplierState = {
	customerSuppliersByCode: {},
	status: LoadingStatus.idle,
};

const SYSCO_DSSI_CODE = 'SYSD';

export const fetchSyscoSupplier = createAsyncThunk<
	ISupplier,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'supplierSlice/fetchSyscoSupplier',
	async (_, { dispatch, rejectWithValue }) => {
		const supplierResponse = await dispatch(
			fetchSupplierByCode(SYSCO_DSSI_CODE)
		);

		if (fetchSupplierByCode.fulfilled.match(supplierResponse)) {
			return supplierResponse.payload;
		}
		rejectWithValue(supplierResponse.payload);
	}
);

export const fetchSupplierByCode = createAsyncThunk<
	ISupplier,
	string,
	{ rejectValue: string[] | Response }
>(
	'supplierSlice/fetchSupplierByCode',
	async (dssiCode: string, { getState, rejectWithValue }) => {
		const state = getState();

		const supplier = selectSupplierByDssiCode(state, dssiCode);

		if (supplier == undefined || supplier == null) {
			try {
				const supplierResponse = await unwrapFetch2(
					Hub.SupplierControllerV3.GetSupplierByDssiCode(dssiCode)
				);

				return supplierResponse;
			} catch (err) {
				if (err instanceof ApiRequestError) {
					return rejectWithValue(err.errors);
				}
				if (err instanceof ApiServerError) {
					return rejectWithValue(err.response);
				}
				throw err;
			}
		}

		return supplier;
	}
);

export const fetchCurrentCustomerSuppliers = createAsyncThunk<
	Record<string, Hub.Supplier>,
	void,
	{ rejectValue: string[] | Response }
>(
	'supplierSlice/fetchCurrentCustomerSuppliers',
	async (_: void, { rejectWithValue }) => {
		try {
			const customerId = LocalStorage.getCustomerId();
			const suppliersResponse = await unwrapFetch2(
				Hub.SupplierController.GetSuppliersByCustomer(customerId)
			);

			const result = suppliersResponse.reduce((map, supplier) => {
				map[supplier.dssiCode] = supplier;
				return map;
			}, {});

			return result;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const fetchSuppliersForCustomer = createAsyncThunk<
	Hub.Supplier[],
	number,
	{ rejectValue: string[] | Response }
>(
	'supplierSlice/fetchSuppliersForCustomer',
	async (customerId: number, { rejectWithValue }) => {
		try {
			const suppliersResponse = await unwrapFetch2(
				Hub.SupplierController.GetSuppliersByCustomer(customerId)
			);

			const uniqueSuppliers: Record<string, Hub.Supplier> = {};

			suppliersResponse.reduce((map, supplier) => {
				map[supplier.dssiCode] = supplier;
				return map;
			}, uniqueSuppliers);

			return Object.values(uniqueSuppliers);
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const suppliersSlice = createSlice({
	name: 'suppliers',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchCurrentCustomerSuppliers.pending, (state) => {
			state.status = LoadingStatus.loading;
			state.customerSuppliersByCode = {};
		});
		builder.addCase(fetchCurrentCustomerSuppliers.rejected, (state) => {
			state.status = LoadingStatus.failure;
			state.customerSuppliersByCode = {};
		});
		builder.addCase(
			fetchCurrentCustomerSuppliers.fulfilled,
			(state, action) => {
				state.status = LoadingStatus.success;
				state.customerSuppliersByCode = action.payload;
			}
		);
	},
});

export function selectSupplierById(state, id: number): ISupplier {
	const suppliers = state.suppliers.customerSuppliersByCode;
	for (const key in suppliers) {
		if (Object.prototype.hasOwnProperty.call(suppliers, key)) {
			if (suppliers[key].id === id) {
				return suppliers[key];
			}
		}
	}
	return null;
}

export function selectSupplierByDssiCode(state, dssiCode: string): ISupplier {
	const suppliers = state.suppliers.customerSuppliersByCode;
	return suppliers[dssiCode];
}

export function selectSuppliers(state): ISupplier[] {
	const supplierArray: ISupplier[] = [];
	const suppliers = state.suppliers.customerSuppliersByCode;
	for (const key in suppliers) {
		if (Object.prototype.hasOwnProperty.call(suppliers, key)) {
			supplierArray.push(suppliers[key]);
		}
	}
	return supplierArray;
}
